import { ArrowRight, Search } from "lucide-react";
import { Dialog as Primitive } from "radix-ui";
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { useUsuario } from "@/features/acceso/api";
import { cn } from "@/lib/cn";
import { useDebounced } from "@/lib/debounce";

import { useBusqueda } from "./busqueda";
import { filtrar, type Opcion, opcionesFijas } from "./opciones";

/**
 * **El buscador de la barra (Ctrl+K)** (#30): escribir y llegar, sin buscar en el menú. Arriba las
 * acciones del día y las pantallas que la persona puede abrir (la misma regla del menú lateral); con
 * dos letras o más, también los pedidos, cotizaciones, clientes y referencias que encuentra el
 * servidor. Flechas para moverse, Enter para abrir, Esc para cerrar.
 *
 * Solo lleva a sitios: ninguna opción guarda ni cambia nada desde aquí.
 */
export function BuscadorDeAcciones({ abierto, onAbiertoChange }: { abierto: boolean; onAbiertoChange: (abierto: boolean) => void }) {
  const usuario = useUsuario().data;
  const navigate = useNavigate();
  const location = useLocation();
  const returnFocus = useRef<HTMLElement | null>(null);
  const [texto, setTexto] = useState("");
  const [activo, setActivo] = useState(0);
  const [aviso, setAviso] = useState("");
  const ubicacion = location.pathname + location.search;
  const fijas = useMemo(() => opcionesFijas(usuario, ubicacion), [usuario, ubicacion]);
  const buscado = useDebounced(texto.trim(), 250);
  const datos = useBusqueda(abierto ? buscado : "", usuario);
  const opciones = [...filtrar(fijas, texto), ...datos.opciones];
  const indice = Math.min(activo, opciones.length - 1);
  const listaId = useId();
  const idDe = (i: number) => `${listaId}-${i}`;

  useEffect(() => {
    // jsdom no lo tiene; en el navegador mantiene a la vista la fila elegida con las flechas.
    document.getElementById(`${listaId}-${indice}`)?.scrollIntoView?.({ block: "nearest" });
  }, [indice, listaId]);

  const cambiar = (siguiente: boolean) => {
    if (!siguiente) {
      setTexto("");
      setActivo(0);
      setAviso("");
    }
    onAbiertoChange(siguiente);
  };
  const ir = (o: Opcion) => {
    if (o.to.includes("?accion=") && document.querySelector('[role="dialog"][data-state="open"]:not([data-buscador])')) {
      setAviso("Cierra el formulario abierto antes de abrir otra acción.");
      return;
    }
    cambiar(false);
    navigate(o.to);
  };
  const teclas = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const paso = e.key === "ArrowDown" ? 1 : -1;
      setActivo(Math.max(0, Math.min(opciones.length - 1, indice + paso)));
    } else if (e.key === "Enter" && opciones[indice]) {
      e.preventDefault();
      ir(opciones[indice]);
    }
  };

  return (
    <Primitive.Root open={abierto} onOpenChange={cambiar}>
      <Primitive.Portal>
        <Primitive.Overlay className="fixed inset-0 z-[60] bg-black/60 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Primitive.Content
          data-buscador
          aria-describedby={undefined}
          onOpenAutoFocus={() => { returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            // La acción elegida ya puso el foco en su sitio (la lupa o «Por programar» del tablero): se respeta.
            const ya = document.activeElement;
            if (ya instanceof HTMLElement && ya !== document.body && ya.isConnected && !ya.closest("[data-buscador]")) return;
            if (returnFocus.current?.isConnected && !returnFocus.current.closest("[inert]")) returnFocus.current.focus({ preventScroll: true });
          }}
          className="panel-light fixed top-[12vh] left-1/2 z-[70] flex max-h-[70vh] w-[min(40rem,calc(100vw-2rem))] -translate-x-1/2 flex-col overflow-hidden rounded-lg text-ink data-[state=open]:animate-in data-[state=open]:fade-in-0"
        >
          <Primitive.Title className="sr-only">Buscar o ir a</Primitive.Title>
          <label className="flex items-center gap-3 border-b border-line px-4">
            <Search aria-hidden className="size-4.5 shrink-0 text-ink-3" />
            <input
              role="combobox"
              aria-label="Buscar acciones, pantallas, pedidos, cotizaciones, clientes o referencias"
              aria-expanded="true"
              aria-controls={listaId}
              aria-activedescendant={opciones.length ? idDe(indice) : undefined}
              autoComplete="off"
              placeholder="Escribe una acción, una pantalla, un cliente o un código…"
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                setActivo(0);
                setAviso("");
              }}
              onKeyDown={teclas}
              className="min-h-14 flex-1 bg-transparent text-base text-ink outline-none placeholder:text-ink-3"
            />
          </label>

          <div id={listaId} role="listbox" aria-label="Resultados" className="flex-1 overflow-y-auto p-2">
            {porGrupo(opciones).map(([grupo, filas]) => (
              <div key={grupo} role="group" aria-label={grupo}>
                <p aria-hidden className="px-3 pt-3 pb-1 text-[10px] font-semibold tracking-[0.16em] text-ink-3 uppercase">
                  {grupo}
                </p>
                {filas.map(([o, i]) => (
                  <div
                    key={o.id}
                    id={idDe(i)}
                    role="option"
                    aria-selected={i === indice}
                    onMouseMove={() => i !== indice && setActivo(i)}
                    onClick={() => ir(o)}
                    className={cn("flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-3 py-1.5", i === indice && "bg-surface-3")}
                  >
                    {o.icon ? <o.icon aria-hidden className="size-4 shrink-0 text-ink-2" /> : <span aria-hidden className="size-4 shrink-0" />}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-ink">{o.label}</span>
                      {o.detalle ? <span className="block truncate text-xs text-ink-3">{o.detalle}</span> : null}
                    </span>
                    {i === indice ? <ArrowRight aria-hidden className="size-4 shrink-0 text-ink-3" /> : null}
                  </div>
                ))}
              </div>
            ))}
            {!opciones.length && !datos.cargando ? <p className="px-3 py-6 text-center text-sm text-ink-3">Nada con «{texto.trim()}».</p> : null}
          </div>

          <footer className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-line px-4 py-2 text-xs text-ink-3">
            {aviso ? <span role="status" className="text-warn">{aviso}</span> : null}
            {datos.cargando ? <span role="status">Buscando…</span> : null}
            {datos.fallo ? <span role="alert" className="text-warn">Algunas búsquedas no respondieron.</span> : null}
            <span className="ml-auto flex gap-3">
              <span><Tecla>↑</Tecla><Tecla>↓</Tecla> moverse</span>
              <span><Tecla>Enter</Tecla> abrir</span>
              <span><Tecla>Esc</Tecla> cerrar</span>
            </span>
          </footer>
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
}

/** Las opciones en sus grupos, en el orden en que llegan, con su posición en la lista entera (las flechas la recorren de corrido). */
function porGrupo(opciones: Opcion[]): [Opcion["grupo"], [Opcion, number][]][] {
  const grupos = new Map<Opcion["grupo"], [Opcion, number][]>();
  opciones.forEach((o, i) => grupos.set(o.grupo, [...(grupos.get(o.grupo) ?? []), [o, i]]));
  return [...grupos];
}

function Tecla({ children }: { children: string }) {
  return <kbd className="mr-1 rounded border border-line bg-surface-2 px-1 font-mono text-[10px] text-ink-2">{children}</kbd>;
}
